import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Payment } from '@shared/types/payment.types'

export type OperationType =
  | 'cancel'
  | 'capture'
  | 'refund'
  | 'retry'
  | 'increment-auth'
  | 'extend-auth'
  | 'flag'
  | 'approve-refund'
  | 'send-receipt'
  | 'allow-list'
  | 'block-list'
  | 'respond-dispute'
  | 'add-note'

interface PaymentOperationsContextValue {
  openOperation: (type: OperationType, payment: Payment) => void
  currentPayment: Payment | null
  setCurrentPayment: (payment: Payment | null) => void
}

const PaymentOperationsContext = createContext<PaymentOperationsContextValue | null>(null)

export function usePaymentOperations() {
  return useContext(PaymentOperationsContext)
}

/**
 * Keeps the operations context in sync with the currently-displayed payment.
 * Call this once per view (panel or full-page) instead of duplicating the
 * useEffect / setCurrentPayment pattern in every component.
 */
export function useSyncCurrentPayment(payment: Payment | null) {
  const ops = usePaymentOperations()
  useEffect(() => {
    ops?.setCurrentPayment(payment ?? null)
    return () => ops?.setCurrentPayment(null)
  }, [payment, ops])
}

interface PaymentOperationsProviderProps {
  children: ReactNode
  onOpenOperation: (type: OperationType, payment: Payment) => void
}

export function PaymentOperationsProvider({
  children,
  onOpenOperation,
}: PaymentOperationsProviderProps) {
  const [currentPayment, setCurrentPayment] = useState<Payment | null>(null)

  return (
    <PaymentOperationsContext.Provider
      value={{ openOperation: onOpenOperation, currentPayment, setCurrentPayment }}
    >
      {children}
    </PaymentOperationsContext.Provider>
  )
}
